var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from 'react';
import Icon from '@icon/Icon';
import classNames from 'classnames';
import ButtonComponent from '@widget/buttons/ButtonComponent';
import Input from '@widget/forms/inputs/Input';
import FormField from '@widget/forms/inputs/FormField';
import Label from '@widget/forms/inputs/Label';
import FormFeedback from '@widget/forms/inputs/FormFeedback';
import Checkbox from '@widget/forms/inputs/Checkbox';
import { useOverlayStack } from '@widget/popups/overlay-stack';
import { toBool } from '@utils';
import { UXPContext } from '@core/context/Context';
function renderPromptWithPill(template, text) {
    const parts = template.split('{text}');
    if (parts.length === 1)
        return template;
    return _jsx(_Fragment, { children: parts.map((part, i) => (_jsxs(React.Fragment, { children: [part, i < parts.length - 1 && (_jsx("span", { className: "uxpcore_alert__verification-pill", children: text }))] }, i))) });
}
const ConfirmAlert = (props) => {
    let { id, title, content, onConfirm, onCancel, confirmButtonTitle, cancelButtonTitle, remove, icon, requireVerification, verificationText, verificationTitle, verificationPrompt, verificationError, verificationConfirmButtonTitle, caseSensitive, fields } = props;
    if (!icon)
        icon = ['fas', 'bell'];
    if (requireVerification && !verificationText)
        verificationText = 'confirm';
    const context = React.useContext(UXPContext);
    const alertRef = React.useRef(null);
    // Optional inline fields (flatten any sections into a flat field list).
    const flatFields = React.useMemo(() => (fields || []).flatMap((f) => (Array.isArray(f === null || f === void 0 ? void 0 : f.fields) ? f.fields : [f])), [fields]);
    const hasFields = flatFields.length > 0;
    const [fieldValues, setFieldValues] = React.useState(() => {
        const init = {};
        flatFields.forEach((f) => { init[f.name] = f.value; });
        return init;
    });
    const [showVerification, setShowVerification] = React.useState(false);
    const [verificationInput, setVerificationInput] = React.useState('');
    const handleRemove = () => {
        alertRef.current.classList.remove("uxpcore_alert--show");
        alertRef.current.classList.add("uxpcore_alert--hide");
        setTimeout(() => {
            remove(id);
        }, 300);
    };
    // Real-time validation
    const isVerificationValid = React.useMemo(() => {
        if (!requireVerification || !showVerification)
            return true;
        const inputValue = caseSensitive ? verificationInput : verificationInput.toLowerCase();
        const expectedValue = caseSensitive ? verificationText : verificationText === null || verificationText === void 0 ? void 0 : verificationText.toLowerCase();
        return inputValue === expectedValue;
    }, [requireVerification, showVerification, verificationInput, verificationText, caseSensitive]);
    function confirm() {
        return __awaiter(this, void 0, void 0, function* () {
            if (requireVerification && !showVerification) {
                // Show verification step
                setShowVerification(true);
                return;
            }
            if (requireVerification && showVerification && !isVerificationValid) {
                // Validation already handled by disabled state
                return;
            }
            handleRemove();
            return onConfirm(hasFields ? fieldValues : undefined);
        });
    }
    function cancel() {
        handleRemove();
        return onCancel();
    }
    const isConfirmDisabled = requireVerification && showVerification && !isVerificationValid;
    // Register in the overlay stack so overlays UNDER this dialog (modal /
    // slide-in panel hosting it) see they're not topmost and ignore Escape —
    // otherwise their earlier-registered global listeners fire first and close
    // the host along with the dialog.
    // On phones, Back cancels it like Escape
    const isTopOverlay = useOverlayStack(true, () => cancel());
    // Keyboard shortcuts: Esc cancels, Enter confirms. Tab cycles between buttons natively.
    React.useEffect(() => {
        const handleKeyDown = (e) => {
            const target = e.target;
            if (e.key === 'Escape') {
                if (e.defaultPrevented || !isTopOverlay())
                    return;
                e.preventDefault();
                cancel();
                return;
            }
            if (e.key === 'Enter') {
                // Let native activation handle Enter on a focused button (incl. Cancel).
                if (target instanceof HTMLButtonElement)
                    return;
                // Don't hijack multi-line input.
                if (target instanceof HTMLTextAreaElement)
                    return;
                if (isConfirmDisabled)
                    return;
                e.preventDefault();
                confirm();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isConfirmDisabled, showVerification, requireVerification, isVerificationValid]);
    // Initial focus lands on the confirm button so Enter activates the primary action.
    // In verification mode the input already auto-focuses.
    React.useEffect(() => {
        var _a;
        if (showVerification)
            return;
        const btn = (_a = alertRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('button.uxpcore_alert__confirm-btn');
        btn === null || btn === void 0 ? void 0 : btn.focus();
    }, [showVerification]);
    return _jsxs("div", { className: classNames('uxpcore_alert', 'uxpcore_alert--confirm-alert', 'uxpcore_alert--show', 'uxpcore_alert--has-footer', {
            'uxpcore_alert--verification-mode': showVerification
        }), ref: alertRef, children: [_jsx("div", { className: "uxpcore_alert__header", children: _jsx("div", { className: "uxpcore_alert__title", children: typeof title == "string" ?
                        _jsxs("div", { className: "uxpcore_alert__title-content", children: [_jsx("div", { className: "uxpcore_alert__icon", children: _jsx(Icon, { icon: icon }) }), _jsx("div", { className: "uxpcore_alert__title-text", children: showVerification && requireVerification ? (verificationTitle || context.$L('uxp-core.text.verification-required')) : title })] })
                        : title() }) }), _jsxs("div", { className: "uxpcore_alert__body", children: [showVerification && requireVerification ? (_jsxs(FormField, { style: { padding: "0 1.25rem" }, children: [_jsx(Label, { children: renderPromptWithPill(verificationPrompt || context.$L('uxp-core.text.type-to-confirm'), verificationText || '') }), _jsx(Input, { value: verificationInput, onChange: (val) => setVerificationInput(val), placeholder: verificationText, isValid: verificationInput.trim() === '' ? undefined : isVerificationValid, inputAttr: { autoFocus: true } }), verificationInput.trim() !== '' && !isVerificationValid && (_jsx(FormFeedback, { validInput: false, children: verificationError || context.$L('uxp-core.text.verification-mismatch') }))] })) : (typeof content == 'string' ? content : content()), hasFields && !(showVerification && requireVerification) && (_jsx("div", { className: "uxpcore_alert__fields", children: flatFields.map((f, i) => {
                            var _a;
                            return ((f.type === 'checkbox' || f.type === 'toggle') ? (_jsx(Checkbox, { type: f.type === 'checkbox' ? 'bordered' : 'switch-line', label: f.label, checked: toBool(fieldValues[f.name]), onChange: (v) => setFieldValues((p) => (Object.assign(Object.assign({}, p), { [f.name]: v }))) }, i)) : (_jsxs(FormField, { children: [_jsx(Label, { children: f.label }), _jsx(Input, { value: String((_a = fieldValues[f.name]) !== null && _a !== void 0 ? _a : ''), onChange: (v) => setFieldValues((p) => (Object.assign(Object.assign({}, p), { [f.name]: v }))) })] }, i)));
                        }) }))] }), _jsxs("div", { className: "uxpcore_alert__footer", children: [_jsx(ButtonComponent, { title: cancelButtonTitle, onClick: cancel, variant: 'secondary', size: "small", className: 'uxpcore_alert__cancel-btn' }), _jsx(ButtonComponent, { title: showVerification && requireVerification ? (verificationConfirmButtonTitle || context.$L('uxp-core.icon-picker.confirm')) : confirmButtonTitle, onClick: confirm, variant: "primary", size: "small", className: "uxpcore_alert__confirm-btn", disabled: isConfirmDisabled })] })] }, id);
};
export default ConfirmAlert;
